import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';
import { subDays, subMonths } from 'date-fns';
import { fetchDashboardData } from '../utils/dashboardHelper';
import { invalidateCache } from '../utils/fyDataCache';
import { useAuth } from './AuthContext';
import { defaultWidgetLayout, dateRangePresets } from '../data/mockDashboardData';

const DashboardContext = createContext(null);

// D2 (2026-07-17): the analytics widget datasets are gone — widgets now fetch
// their own small data via dashboardWidgetData.js. Context data is just the
// (currently empty) notifications payload.
const emptyData = {
  notifications: []
};

export const DashboardProvider = ({ children }) => {
  const { activeCompany, activeFinancialYear, activeBranch } = useAuth();

  // Date range state
  const [dateRange, setDateRange] = useState({
    preset: 'last30days',
    startDate: subDays(new Date(), 30),
    endDate: new Date()
  });

  // Widget visibility state (D2 set: analytics widgets removed 2026-07-17;
  // their full gated reports remain in the Reports menu)
  const [widgetVisibility, setWidgetVisibility] = useState({
    'executive-summary': true,
    'whats-new': true,
    'kpi-cards': true,
    'sales-overview': true,
    'money-position': true,
    'receivable-payable': true,
    'sales-recovery': true,
    'sales-expense': true,
    'activity-feed': true
  });

  // Widget layout state
  const [widgetLayout, setWidgetLayout] = useState(defaultWidgetLayout);

  // Loading states
  const [isLoading, setIsLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Notification state
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);

  // Dashboard data state - starts with empty data so widgets don't crash
  const [data, setData] = useState(emptyData);

  // Computed values
  const unreadNotifications = useMemo(() =>
    notifications.filter(n => !n.read).length,
    [notifications]
  );

  // ── Fetch real data from Firestore ──────────────────────────────────────
  const loadDashboardData = useCallback(async (start, end, forceRefresh = false) => {
    if (!activeCompany?.id) return;

    try {
      const fyId = activeFinancialYear?.id || null;
      const branchId = (activeBranch && activeBranch.id !== 'all') ? activeBranch.id : null;
      const result = await fetchDashboardData(activeCompany.id, fyId, start, end, branchId, activeCompany?.currencySymbol || 'Rs.', forceRefresh);

      if (result) {
        setData(result);
        if (result.notifications) {
          setNotifications(result.notifications);
        }
      }
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      // Keep existing data on error rather than resetting
    }
  }, [activeCompany, activeFinancialYear, activeBranch]);

  // Load data on mount and when company/FY changes
  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setIsLoading(true);
      await loadDashboardData(dateRange.startDate, dateRange.endDate);
      if (!cancelled) {
        setIsLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [loadDashboardData]);

  // Actions
  const changeDateRange = useCallback((preset, customStartDate = null, customEndDate = null) => {
    let start, end;

    if (preset === 'custom' && customStartDate && customEndDate) {
      start = customStartDate;
      end = customEndDate;
    } else {
      const presetConfig = dateRangePresets.find(p => p.value === preset);
      if (presetConfig) {
        start = presetConfig.startDate;
        end = presetConfig.endDate;
      }
    }

    if (start && end) {
      setDateRange({ preset, startDate: start, endDate: end });
      // Pass dates directly to avoid stale closure issue
      setRefreshing(true);
      loadDashboardData(start, end).finally(() => setRefreshing(false));
    }
  }, [loadDashboardData]);

  const toggleWidget = useCallback((widgetId) => {
    setWidgetVisibility(prev => ({
      ...prev,
      [widgetId]: !prev[widgetId]
    }));
  }, []);

  const updateWidgetLayout = useCallback((newLayout) => {
    setWidgetLayout(newLayout);
    localStorage.setItem('dashboardLayout', JSON.stringify(newLayout));
  }, []);

  const resetWidgetLayout = useCallback(() => {
    setWidgetLayout(defaultWidgetLayout);
    localStorage.removeItem('dashboardLayout');
  }, []);

  const markNotificationRead = useCallback((notificationId) => {
    setNotifications(prev =>
      prev.map(n =>
        n.id === notificationId ? { ...n, read: true } : n
      )
    );
  }, []);

  const markAllNotificationsRead = useCallback(() => {
    setNotifications(prev =>
      prev.map(n => ({ ...n, read: true }))
    );
  }, []);

  const clearNotification = useCallback((notificationId) => {
    setNotifications(prev =>
      prev.filter(n => n.id !== notificationId)
    );
  }, []);

  const refreshDashboard = useCallback(async () => {
    setRefreshing(true);
    // Hard refresh: drop every cached dashboard dataset for this company
    // (main data + KPI cards + widgets), then fetch live.
    if (activeCompany?.id) invalidateCache(`dashRaw|${activeCompany.id}`);
    await loadDashboardData(dateRange.startDate, dateRange.endDate, true);
    setRefreshing(false);
  }, [loadDashboardData, dateRange, activeCompany?.id]);

  const toggleSearch = useCallback(() => {
    setShowSearch(prev => !prev);
    if (showSearch) {
      setSearchQuery('');
    }
  }, [showSearch]);

  const toggleNotifications = useCallback(() => {
    setShowNotifications(prev => !prev);
  }, []);

  const performSearch = useCallback((query) => {
    setSearchQuery(query);
  }, []);

  const value = useMemo(() => ({
    // Data
    data,
    dateRange,
    widgetVisibility,
    widgetLayout,
    notifications,
    unreadNotifications,
    searchQuery,
    showSearch,
    showNotifications,

    // Loading states
    isLoading,
    refreshing,

    // Actions
    changeDateRange,
    toggleWidget,
    updateWidgetLayout,
    resetWidgetLayout,
    markNotificationRead,
    markAllNotificationsRead,
    clearNotification,
    refreshDashboard,
    toggleSearch,
    toggleNotifications,
    performSearch,

    // Presets
    dateRangePresets
  }), [
    data,
    dateRange,
    widgetVisibility,
    widgetLayout,
    notifications,
    unreadNotifications,
    searchQuery,
    showSearch,
    showNotifications,
    isLoading,
    refreshing,
    changeDateRange,
    toggleWidget,
    updateWidgetLayout,
    resetWidgetLayout,
    markNotificationRead,
    markAllNotificationsRead,
    clearNotification,
    refreshDashboard,
    toggleSearch,
    toggleNotifications,
    performSearch
  ]);

  return (
    <DashboardContext.Provider value={value}>
      {children}
    </DashboardContext.Provider>
  );
};

export const useDashboard = () => {
  const context = useContext(DashboardContext);
  if (!context) {
    throw new Error('useDashboard must be used within a DashboardProvider');
  }
  return context;
};

export default DashboardContext;
